import _ from 'lodash';
import { ControlFrameworksListQuery } from 'src/graphql/_generated';

type FrameworkLibraryItem = ControlFrameworksListQuery['controlFrameworks'][number];

export const MAX_PINNED_FRAMEWORKS = 3;

export const sanitizePinnedFrameworkIds = ({
  frameworks,
  primaryFrameworkId,
  pinnedFrameworkIds,
}: {
  frameworks: FrameworkLibraryItem[];
  primaryFrameworkId?: string | null;
  pinnedFrameworkIds?: string[] | null;
}) => {
  const frameworkIds = new Set(frameworks.map((framework) => framework.id));
  const primaryCountsTowardLimit = primaryFrameworkId && frameworkIds.has(primaryFrameworkId);
  const maxAdditionalPinned = MAX_PINNED_FRAMEWORKS - (primaryCountsTowardLimit ? 1 : 0);

  return _.uniq((pinnedFrameworkIds || []).filter((id) => id !== primaryFrameworkId && frameworkIds.has(id))).slice(
    0,
    Math.max(maxAdditionalPinned, 0),
  );
};

export const getOrderedFrameworks = ({
  frameworks,
  primaryFrameworkId,
  pinnedFrameworkIds,
}: {
  frameworks: FrameworkLibraryItem[];
  primaryFrameworkId?: string | null;
  pinnedFrameworkIds: string[];
}) => {
  const sortedFrameworks = _.orderBy(frameworks, [(item) => item.shortname?.toLowerCase() || '']);
  const frameworkById = new Map(sortedFrameworks.map((framework) => [framework.id, framework]));
  const prioritizedIds = [
    ...(primaryFrameworkId && frameworkById.has(primaryFrameworkId) ? [primaryFrameworkId] : []),
    ...pinnedFrameworkIds,
  ];
  const prioritizedIdSet = new Set(prioritizedIds);

  return [
    ...prioritizedIds.map((id) => frameworkById.get(id)).filter((framework): framework is FrameworkLibraryItem => !!framework),
    ...sortedFrameworks.filter((framework) => !prioritizedIdSet.has(framework.id)),
  ];
};

export const reorderPinnedFrameworks = ({
  pinnedFrameworkIds,
  sourceFrameworkId,
  targetFrameworkId,
}: {
  pinnedFrameworkIds: string[];
  sourceFrameworkId: string;
  targetFrameworkId: string;
}) => {
  if (sourceFrameworkId === targetFrameworkId) return pinnedFrameworkIds;

  const nextPinnedFrameworkIds = pinnedFrameworkIds.filter((id) => id !== sourceFrameworkId);
  const targetIndex = nextPinnedFrameworkIds.indexOf(targetFrameworkId);

  if (targetIndex === -1) return pinnedFrameworkIds;

  nextPinnedFrameworkIds.splice(targetIndex, 0, sourceFrameworkId);
  return nextPinnedFrameworkIds;
};
